import React from 'react'
import styled from '@emotion/styled'
import { toMoney } from '@mobi/utils/money'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { colors, spacing } from '@mobi/component-library/Foundations'
import { isKambiSportsSingleSelection } from '@mobi/betslip/helpers/typeGuards'
import type { KambiSportsEventDetails, KambiSportsMultiSelection } from '@mobi/betslip/types'
import { TeamsIcon } from '@mobi/sports-offering/Components/Common/TeamsIcon'
import { formatKambiCurrencyToDollarDecimal } from '@mobi/sports-offering/helpers/currency/currencyFormat'
import { SecondaryInfo } from '@core/Areas/Quickbet/Components/BetSelectionInfo/Components/Common/SecondaryInfo'
import { SelectionInfo } from '@core/Areas/Quickbet/Components/BetSelectionInfo/Components/Common/SelectionInfo'
import { SelectionOverflow } from '@core/Areas/Quickbet/Components/BetSelectionInfo/Components/Common/SelectionOverflow'
import { MultiBetLegPriceStyled } from '@core/Areas/Quickbet/Components/BetSelectionInfo/Components/Common/BetSelectionInfo.styles'
import { getSelectionNameFromKambiBetOffer } from '@core/Areas/Quickbet/helpers/selectionsKambi'

export const KambiSportsHeaderMulti: React.FC<{
  selection: KambiSportsMultiSelection
  eventDetails: KambiSportsEventDetails
}> = ({ selection, eventDetails }) => {
  return (
    <WrapperStyled>
      <SecondaryInfo marketInfo={`${selection.legs.length} Leg Multi`} />

      <SelectionOverflow shouldRestrictHeight={shouldRestrictHeightForMulti(selection.legs)}>
        <Flex flexDirection='column' gap='1rem'>
          {selection.legs.map(leg => {
            if (isKambiSportsSingleSelection(leg)) {
              const event = eventDetails.events.find(event => event.id === leg.betOffer.eventId)
              const leagueId = event?.path.at(-1)?.id
              const sportName = event?.sport

              return (
                <React.Fragment key={leg.betOffer.id}>
                  <Flex flexDirection='column' gap={spacing.smx1}>
                    <SecondaryInfo
                      marketInfo={
                        <>
                          <Icon type='tab' size='1.6rem' />
                          <span>{event?.englishName}</span>
                        </>
                      }
                    />

                    <SelectionInfo
                      leftSlot={
                        <div
                          style={{
                            width: '3rem',
                            height: '3rem',
                            alignItems: 'center',
                            justifyContent: 'center',
                            display: 'flex',
                            borderRadius: '50%',
                            background: '#47245D',
                            flexShrink: 0,
                          }}
                        >
                          <TeamsIcon
                            leagueId={leagueId}
                            participantId={leg.betOffer.outcomes[0].participantId}
                            betOfferType={leg.betOffer.betOfferType.id}
                            betOfferId={leg.betOffer.id}
                            outcomeId={leg.betOffer.outcomes[0].id}
                            size='1.4rem'
                            teamName={leg.betOffer.outcomes[0].participant}
                            sportName={sportName}
                          />
                        </div>
                      }
                      text={getSelectionNameFromKambiBetOffer(leg.betOffer)}
                      subText={leg.betOffer.criterion.label}
                      rightSlot={
                        <MultiBetLegPriceStyled>
                          {toMoney(
                            formatKambiCurrencyToDollarDecimal(leg.betOffer.outcomes[0].odds)
                          )}
                        </MultiBetLegPriceStyled>
                      }
                    />
                  </Flex>
                  <div className='multi-info__divider' />
                </React.Fragment>
              )
            }

            const event = eventDetails.events.find(event => event.id === leg.betOffers[0].eventId)
            const leagueId = event?.path.at(-1)?.id
            const sportName = event?.sport

            return (
              <React.Fragment key={leg.betOffers.map(offer => offer.id).join('')}>
                <Flex flexDirection='column' gap={spacing.smx1}>
                  <SecondaryInfo
                    marketInfo={
                      <>
                        <Icon type='tab' size='1.6rem' />
                        <span>{event?.englishName}</span>
                      </>
                    }
                  />

                  <Flex justifyContent='space-between' alignItems='center' gap={spacing.smx2}>
                    <Flex flexDirection='column' gap={spacing.smx1}>
                      {leg.betOffers.map(offer => (
                        <SelectionInfo
                          key={offer.id}
                          leftSlot={
                            <div
                              style={{
                                width: '3rem',
                                height: '3rem',
                                alignItems: 'center',
                                justifyContent: 'center',
                                display: 'flex',
                              }}
                            >
                              <TeamsIcon
                                leagueId={leagueId}
                                participantId={offer.outcomes[0].participantId}
                                betOfferType={offer.betOfferType.id}
                                betOfferId={offer.id}
                                outcomeId={offer.outcomes[0].id}
                                size='1.6rem'
                                teamName={offer.outcomes[0].participant}
                                sportName={sportName}
                              />
                            </div>
                          }
                          text={getSelectionNameFromKambiBetOffer(offer)}
                          subText={offer.criterion.label}
                        />
                      ))}
                    </Flex>

                    <MultiBetLegPriceStyled>
                      {toMoney(formatKambiCurrencyToDollarDecimal(leg.odds))}
                    </MultiBetLegPriceStyled>
                  </Flex>
                </Flex>
                <div className='multi-info__divider' />
              </React.Fragment>
            )
          })}
        </Flex>
      </SelectionOverflow>
    </WrapperStyled>
  )
}

// =============
// Local Helpers
// =============

function shouldRestrictHeightForMulti(legs: KambiSportsMultiSelection['legs']) {
  let currentOutcomeCount = 0
  let shouldRestrictHeight = false

  for (const leg of legs) {
    if (leg.betSelectionType === 'same-game-multi') {
      currentOutcomeCount += leg.betOffers.length
    } else {
      currentOutcomeCount += 1
    }

    if (currentOutcomeCount > 3) {
      shouldRestrictHeight = true
      break
    }
  }

  return shouldRestrictHeight
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.sm,
  paddingBottom: spacing.smx2,

  '.multi-info__divider': {
    height: '1px',
    width: '100%',
    backgroundColor: colors.studio[600],

    '&:last-of-type': { display: 'none' },
  },
})
